import * as constants from '@/constants';

export const utilSetLocalStorage = (storageKey: string, storageValue: string) => {
  localStorage.setItem(storageKey, storageValue);
};

export const utilGetLocalStorage = (storageKey: string) => localStorage.getItem(storageKey);

export const utilCheckUserSubscribed = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  if (userType &&
    (JSON.parse(userType) === constants.SUBSCRIBER_USER_TYPE ||
      JSON.parse(userType) === constants.SUBSCRIBER_SUPER_USER_TYPE ||
      JSON.parse(userType) === constants.SUBSCRIBER_MEDICAL_DEVICE_USER_TYPE ||
      JSON.parse(userType) === constants.USER_MANAGEMENT_USER_TYPE ||
      JSON.parse(userType) === constants.SUBSCRIBER_USER_TYPE_FOR_WEEKLY_UPDATES)) {
    return true;
  }
  return false;
};

/**
 * True for the watchlist-tracking user type (88888), or for an explicitly
 * allowlisted email (e.g. early access for a specific tester), regardless of
 * their actual user type.
 *
 * Gates features that are not yet released to customers — currently the
 * "Drug Products" tab on the site detail page. Kept here beside the other
 * user-type checks rather than inlining `String(user_type) === '88888'` at the
 * call site, which is how the same test ended up duplicated in
 * customAnalytics and foiaRequests.
 *
 * JSON.parse is wrapped: 'UserType' is not always valid JSON (it can hold a
 * bare string), and an exception here would blank the whole page.
 */
export const utilCheckWatchlistTrackingUser = (): boolean => {
  const userEmail = utilGetLocalStorage('User');
  if (userEmail && constants.WATCHLIST_TRACKING_ALLOWLISTED_EMAILS.includes(userEmail)) {
    return true;
  }

  const userType = utilGetLocalStorage('UserType');
  if (!userType) return false;
  let parsed: any = userType;
  try {
    parsed = JSON.parse(userType);
  } catch (e) {
    parsed = userType;
  }
  return String(parsed) === String(constants.WATCHLIST_TRACKING_USER_TYPE);
};

/** True only for user type 88888 itself (no email allowlist) — e.g. the FDA BMIS page and site tab. */
export const utilCheckInternalUserType = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  if (!userType) return false;
  let parsed: any = userType;
  try {
    parsed = JSON.parse(userType);
  } catch (e) {
    parsed = userType;
  }
  return String(parsed) === String(constants.WATCHLIST_TRACKING_USER_TYPE);
};

/**
 * True for the RxLabel Intelligence / USFDA-labelling user type (44444).
 * Used to hide the label_reader admin-only tabs (Rule library, AI
 * fine-tuning) from this cohort -- they get the base label_reader tabs
 * via has_full_access server-side, but not the admin ones, so the menu
 * should match that rather than showing tabs that would 403 on use.
 */
export const utilCheckRxLabelUsfdaUserType = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  if (!userType) return false;
  let parsed: any = userType;
  try {
    parsed = JSON.parse(userType);
  } catch (e) {
    parsed = userType;
  }
  return String(parsed) === String(constants.RXLABEL_USFDA_USER_TYPE);
};

export const utilCheckUserPromotionalModal = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  const parsedUserType = userType ? JSON.parse(userType) : null;
  if (
    parsedUserType === constants.SUBSCRIBER_USER_TYPE ||
    parsedUserType === constants.SUBSCRIBER_SUPER_USER_TYPE ||
    parsedUserType === constants.DATA_TYPING_USER_TYPE ||
    parsedUserType === constants.SUBSCRIBER_MEDICAL_DEVICE_USER_TYPE ||
    parsedUserType === constants.USER_MANAGEMENT_USER_TYPE ||
    parsedUserType === constants.SUBSCRIBER_USER_TYPE_FOR_WEEKLY_UPDATES
  ) {
    return true;
  }
  return false;
};

/** /companyProfile pages (company list, company and organization detail): subscribers plus the
 *  watchlist-tracking type (88888), which the backend already counts as subscribed. */
export const utilCheckCompanyProfileAccess = (): boolean =>
  utilCheckUserSubscribed() || utilCheckWatchlistTrackingUser();

export const utilCheckSuperUserSubscribed = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  if (userType && (JSON.parse(userType) === constants.SUBSCRIBER_SUPER_USER_TYPE ||
   JSON.parse(userType) === constants.SUBSCRIBER_MEDICAL_DEVICE_USER_TYPE || JSON.parse(userType) === constants.USER_MANAGEMENT_USER_TYPE ||
   JSON.parse(userType) === constants.SUBSCRIBER_USER_TYPE_FOR_WEEKLY_UPDATES)) {
    return true;
  }

  return false;
};

export const utilCheck1111SuperUserSubscribed = (): boolean => {
  const userType = utilGetLocalStorage('UserType');
  if (userType && (JSON.parse(userType) === constants.SUBSCRIBER_MEDICAL_DEVICE_USER_TYPE)) {
    return true;
  }

  return false;
};


export const utilRemoveItemLocalStorage = (key: string) => {
  localStorage.removeItem(key);
};

export const utilClearLocalStorage = () => {
  localStorage.clear();
};

export const utilCheckBlockedUserType = () => {
  const userType = utilGetLocalStorage('UserType');
  if (userType && (JSON.parse(userType) === constants.BLOCKED_USER_TYPE)) {
    return true;
  }

  return false;
}
